/**
 * COMPTABILITÉ — L'ASSISTANT DE CLÔTURE.
 *
 * ═══ UNE ROUTE, PAS UN ÉTAT VOLATILE ═══
 *
 * L'étape courante vit dans l'URL (`…/clotures/<nuit>/<étape>`), jamais dans
 * un `useState` qui disparaîtrait au rechargement. Ce n'est pas un
 * raffinement : une clôture se fait à 6 h du matin, souvent en plusieurs fois,
 * parfois à deux. Un état perdu au rechargement, c'est un RECOMPTAGE
 * D'ESPÈCES. Le lien est donc partageable, l'onglet refermable.
 *
 * ═══ RETOUR = QUITTER L'ASSISTANT (07/10/2026) ═══
 *
 * Changer d'étape REMPLACE l'adresse au lieu d'empiler une page d'historique.
 * Avec le rail, on saute d'une étape à l'autre dix fois par clôture : chaque
 * saut empilé faisait du « Retour » (celui de la page comme celui du
 * navigateur) un « étape précédente », et il fallait dix retours pour revenir
 * d'où l'on venait — l'accueil de la Comptabilité, la fiche de la nuit. Le
 * retour mène désormais là, comme partout ailleurs dans Gestion ; reculer
 * d'une étape, c'est le bouton « Précédent » ou le rail.
 *
 * Remplacer l'adresse ne passe pas par le routeur, et c'est voulu : la
 * coquille remonte toute la route à chaque changement de chemin, ce qui
 * rechargeait la clôture, la nuit et les référentiels à chaque étape et
 * rejouait l'entrée du bandeau et du rail. Seul le contenu de l'étape change.
 *
 * ═══ LE PASSAGE D'UNE ÉTAPE À L'AUTRE (07/10/2026) ═══
 *
 * Le contenu de l'étape entre en fondu, avec un léger glissement dans le
 * sens de la marche : vers la gauche quand on avance, vers la droite quand
 * on recule (`sensDuPassage`, closing-rail-model). Il est enveloppé dans un
 * bloc à clé — l'étape — qui renaît à chaque changement ; le pied
 * Précédent / Suivant est HORS de ce bloc : il ne bouge pas. Rien n'attend la
 * fin de l'animation (un onglet caché la gèle), et le mouvement réduit la
 * remplace par un simple fondu.
 *
 * ═══ LA MISE EN PAGE (rail latéral, choisi par l'exploitant) ═══
 *
 * En haut, le bandeau de la nuit (`ClosingHero`) : le visuel de la soirée en
 * fond, et les chiffres clés. Dessous, deux colonnes : le rail des étapes et
 * leur état (`ClosingRail`), collant, à gauche ; l'étape elle-même à droite —
 * les composants `ClosingStep*`, inchangés. En largeur étroite, le rail
 * devient une bande défilante au-dessus de l'étape.
 *
 * Le seul précédent du dépôt (`VenueWizard`) est un état React dans un panneau
 * latéral : il n'est PAS copié. On lui reprend en revanche son atomicité —
 * rien de définitif avant la validation finale.
 *
 * ═══ CONCURRENCE ═══
 *
 * Chaque écriture emporte la version connue. Si quelqu'un d'autre a écrit
 * entre-temps, le serveur refuse — et l'écran le DIT, avec le moyen de
 * repartir de la version fraîche. Écraser silencieusement le travail de
 * l'autre personne aurait été le seul comportement inacceptable.
 *
 * ═══ POINTS D'APPUI SUPPOSÉS ═══
 *
 * Les routes sont écrites en parallèle (contrat §8). Quand l'une manque,
 * l'assistant affiche une phrase honnête au lieu d'un zéro : il ne prétend
 * jamais avoir lu ce qu'il n'a pas reçu.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { AlertTriangle, ArrowLeft, ArrowRight, Check, RefreshCw, Users } from 'lucide-react'
import { Link, usePath } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { noterAdresse } from '../navigation/adresse-courante.js'
import { useAdresseCourante } from '../navigation/use-adresse-courante.js'
import { setPageTitle } from '../prefs.js'
import { adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import {
  COUPURES_DEFAUT, EnCours, Indisponible,
  SEUILS_DEFAUT, commeObjet, ilYA, lireJson, nuitCourte,
  type ClosingApi, type ClosingDetail, type DiscrepancyReasonRow, type Etat,
  type FinanceSettingsView, type PaymentMethodRow, type PointOfSaleRow,
  type RegisterRow, type TerminalRow,
} from './ClosingCommon.js'
import { commeNuit, nuitDeReference, slugSoiree, urlCloture, type NightDetailResponse } from './nights-common.js'
import type { DocVisible } from './SourceViewer.js'
import { ClosingStepNight, doitAmorcer } from './ClosingStepNight.js'
import { ClosingStepPos } from './ClosingStepPos.js'
import { ClosingStepEcarts, ClosingStepSummary, useGesteCloture } from './ClosingStepSummary.js'
import { DecisionCloture, EtatDuPied } from './DecisionCloture.js'
import { NightRecap } from './NightRecap.js'
import { ClosingHero } from './ClosingHero.js'
import { ClosingRail, etatsDesEtapes, type EtapeAssistant } from './ClosingRail.js'
import { sensDuPassage } from './closing-rail-model.js'
import './finance-wizard.css'

/* ═══════════════════ l'étape vit dans l'URL, et nulle part ailleurs ═══════ */

/*
 * Le routeur du dépôt n'observe que `location.pathname` : un changement de
 * paramètre de recherche ne réveille personne. On tient donc ici un abonnement
 * minuscule à NOTRE paramètre — sans toucher au routeur, qui n'appartient à
 * personne dans ce lot.
 */
const abonnesEtape = new Set<() => void>()
function notifierEtape(): void { for (const f of abonnesEtape) f() }
window.addEventListener('popstate', notifierEtape)

/**
 * L'étape vit dans le CHEMIN, lisible : `/comptabilite/clotures/<nuit>/bar-room-1`.
 * L'ancien `?etape=<identifiant>` est encore lu — et réécrit.
 */
function lireEtapeUrl(): string | null {
  const seg = window.location.pathname.split('/')[4]
  return seg ? decodeURIComponent(seg) : new URLSearchParams(window.location.search).get('etape')
}

/** `/comptabilite/clotures/<référence>` — sans l'étape. */
function baseCloture(): string {
  return window.location.pathname.split('/').slice(0, 4).join('/')
}

function useEtapeUrl(): string | null {
  return useSyncExternalStore(
    (cb) => { abonnesEtape.add(cb); return () => { abonnesEtape.delete(cb) } },
    lireEtapeUrl,
  )
}

/**
 * Changer d'étape REMPLACE l'adresse (voir l'en-tête) : l'étape reste dans le
 * chemin — rechargeable, partageable — sans empiler d'historique, et sans
 * réveiller le routeur, qui remonterait toute la page.
 */
function allerEtape(etape: string): void {
  const url = `${baseCloture()}/${encodeURIComponent(etape)}`
  if (`${window.location.pathname}${window.location.search}` === url) return
  window.history.replaceState(window.history.state, '', url)
  // Le routeur ne suit que ses navigations : l'adresse complète est notée
  // pour qu'une garde qui refuse un Retour remette CELLE-CI (adresse-courante.ts).
  noterAdresse(url)
  notifierEtape()
}

/* ═══════════════════════ la page ═══════════════════════ */

/**
 * La page reçoit une RÉFÉRENCE : la nuit (« 2026-09-26-the-culture-… », la
 * forme lisible) ou l'identifiant (anciens liens, révisions remplacées). Une
 * nuit se résout en SA clôture courante ; l'adresse est ensuite réécrite dans
 * sa forme lisible, sans empiler d'historique.
 */
export function FinanceClosingWizardPage({ reference }: { reference: string }) {
  const toast = useToast()
  const { permissions, loading: droitsEnCours } = usePermissions()
  const peut = useCallback((cle: string) => permissions.includes(cle), [permissions])
  const nuitRef = nuitDeReference(reference)
  const [closingId, setClosingId] = useState<string | null>(nuitRef ? null : reference)

  const [detail, setDetail] = useState<Etat<ClosingDetail>>({ etat: 'chargement' })
  const [reglages, setReglages] = useState<FinanceSettingsView>({
    denominations_cents: COUPURES_DEFAUT,
    discrepancy_warn_cents: SEUILS_DEFAUT.warn,
    discrepancy_alert_cents: SEUILS_DEFAUT.alert,
  })
  const [nuit, setNuit] = useState<Etat<NightDetailResponse>>({ etat: 'chargement' })
  const [posDispo, setPosDispo] = useState<PointOfSaleRow[]>([])
  const [terminaux, setTerminaux] = useState<TerminalRow[]>([])
  const [moyens, setMoyens] = useState<PaymentMethodRow[]>([])
  const [motifsEcart, setMotifsEcart] = useState<DiscrepancyReasonRow[]>([])
  const [sauvegarde, setSauvegarde] = useState<'repos' | 'encours' | 'ok'>('repos')
  const [conflit, setConflit] = useState(false)
  /*
   * Reprendre après un conflit REMONTE l'étape. Les brouillons de saisie
   * (quantités de coupures, montants constatés) vivent dans l'étape pour ne
   * pas être arrachés des mains à chaque rafraîchissement : après un refus,
   * ils doivent au contraire repartir de la version fraîche, sans quoi la
   * prochaine écriture réessaierait d'imposer ce que le serveur vient de
   * refuser.
   */
  const [graine, setGraine] = useState(0)

  const etapeUrl = useEtapeUrl()

  /* L'APERÇU DU SCEAU (QA, 11/10/2026) — `?apercu=finalisation` rejoue la
     fin d'une finalisation sur une nuit DÉJÀ finalisée, sans rien écrire
     (`useGesteCloture`). Lu une fois à l'ouverture, puis retiré de l'adresse :
     un rechargement ne le rejoue pas. Un paramètre d'URL n'est jamais un
     booléen : seule la valeur exacte compte. */
  const [apercu] = useState(() => new URLSearchParams(window.location.search).get('apercu') === 'finalisation')
  useEffect(() => {
    if (!apercu) return
    const q = new URLSearchParams(window.location.search)
    q.delete('apercu')
    const reste = q.toString()
    const url = `${window.location.pathname}${reste ? `?${reste}` : ''}`
    window.history.replaceState(window.history.state, '', url)
    noterAdresse(url)
  }, [apercu])

  /* ─── la référence : une nuit se résout en sa clôture courante ─── */
  useEffect(() => {
    if (!nuitRef) { setClosingId(reference); return }
    setClosingId(null)
    setDetail({ etat: 'chargement' })
    void lireJson<NightDetailResponse>(`/api/v1/finance/nights/${nuitRef}`, commeNuit).then((r) => {
      if (r.etat === 'chargement') return
      if (r.etat === 'absent') { setDetail({ etat: 'absent', message: r.message }); return }
      if (!r.valeur.closing) {
        setDetail({ etat: 'absent', message: `Aucune clôture n’est ouverte pour la nuit du ${nuitCourte(nuitRef)}. Elle se commence depuis la fiche de la nuit.` })
        return
      }
      setClosingId(r.valeur.closing.id)
    })
  }, [reference, nuitRef])

  /* ─── lecture ─── */
  const chargerDetail = useCallback(() => {
    if (!closingId) return
    void lireJson<ClosingDetail>(
      `/api/v1/finance/closings/${closingId}`,
      commeObjet(['closing', 'detail'], 'business_date'),
    ).then((r) => { setDetail(r); if (r.etat === 'ok') setConflit(false) })
  }, [closingId])

  useEffect(chargerDetail, [chargerDetail])

  useEffect(() => {
    void lireJson<FinanceSettingsView>('/api/v1/finance/settings', (v) => {
      const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null
      const s = o && typeof o['settings'] === 'object' && o['settings'] !== null
        ? (o['settings'] as Record<string, unknown>) : o
      if (!s) return null
      const coupures = Array.isArray(s['denominations_cents'])
        ? (s['denominations_cents'] as unknown[]).filter((x): x is number => typeof x === 'number' && x > 0)
        : []
      const nombre = (k: string, d: number): number => (typeof s[k] === 'number' ? (s[k] as number) : d)
      return {
        denominations_cents: coupures.length > 0 ? coupures : COUPURES_DEFAUT,
        discrepancy_warn_cents: nombre('discrepancy_warn_cents', SEUILS_DEFAUT.warn),
        discrepancy_alert_cents: nombre('discrepancy_alert_cents', SEUILS_DEFAUT.alert),
      }
    }).then((r) => { if (r.etat === 'ok') setReglages(r.valeur) })

    /* Les référentiels voyagent ENSEMBLE : une seule lecture sert les points
       de vente, les caisses, les terminaux, les moyens de paiement et les
       motifs d'écart. Cinq appels auraient donné cinq occasions d'arriver dans
       le désordre — et les motifs d'écart, servis ici depuis toujours, ne
       demandaient qu'à être lus. */
    void lireJson<{
      points_of_sale: PointOfSaleRow[]; registers: RegisterRow[]; terminals: TerminalRow[]
      payment_methods: PaymentMethodRow[]; discrepancy_reasons: DiscrepancyReasonRow[]
    }>('/api/v1/finance/references', (v) => {
      const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null
      if (!o || !Array.isArray(o['points_of_sale'])) return null
      const liste = <T,>(k: string): T[] => (Array.isArray(o[k]) ? (o[k] as T[]) : [])
      return {
        points_of_sale: liste<PointOfSaleRow>('points_of_sale'),
        registers: liste<RegisterRow>('registers'),
        terminals: liste<TerminalRow>('terminals'),
        payment_methods: liste<PaymentMethodRow>('payment_methods'),
        discrepancy_reasons: liste<DiscrepancyReasonRow>('discrepancy_reasons'),
      }
    }).then((r) => {
      if (r.etat !== 'ok') return
      setPosDispo(r.valeur.points_of_sale)
      setTerminaux(r.valeur.terminals)
      setMoyens(r.valeur.payment_methods)
      setMotifsEcart(r.valeur.discrepancy_reasons)
    })
  }, [])

  /*
   * LA NUIT, CONSOLIDÉE PAR LE SERVEUR : ce que les rapports annoncent par point
   * de vente (principal + compléments additionnés), les redditions, le guichet
   * Shotgun, les pièces. Une lecture — là où l'assistant lisait jadis chaque
   * document un par un, annexes comprises (une trentaine par nuit).
   */
  const nuitDeLaCloture = detail.etat === 'ok' ? detail.valeur.business_date : null
  const chargerNuit = useCallback(() => {
    if (!nuitDeLaCloture) return
    void lireJson<NightDetailResponse>(`/api/v1/finance/nights/${nuitDeLaCloture}`, commeNuit).then(setNuit)
  }, [nuitDeLaCloture])
  useEffect(chargerNuit, [chargerNuit])

  /* ─── écriture, avec la version connue ─── */
  const ecrire = useCallback(async (
    method: string, chemin: string, corps: Record<string, unknown>,
    surRefus?: (erreur: string | undefined) => boolean,
  ): Promise<boolean> => {
    const version = detail.etat === 'ok' ? detail.valeur.version : undefined
    setSauvegarde('encours')
    const r = await adminMutate<{ error?: string; message?: string }>(
      method, chemin, { ...corps, expected_version: version },
    )
    const passe = r.status >= 200 && r.status < 300 && r.data.error === undefined
    if (passe) {
      setSauvegarde('ok')
      chargerDetail()
      return true
    }
    setSauvegarde('repos')
    if (surRefus?.(r.data.error)) return false
    // UNE CONFIRMATION DEMANDÉE N'EST PAS UN CONFLIT (16/10/2026) : le 409
    // FINANCE_CONFIRMATION dit « finaliser ferait baisser une nuit validée,
    // donnez un motif » — personne n'a modifié la clôture. Le message du
    // serveur, et la relecture qui apporte la validation à remplacer.
    if (r.data.error === 'FINANCE_CONFIRMATION') {
      toast('error', r.data.message ?? 'Cette finalisation demande un motif.')
      chargerDetail()
      return false
    }
    if (r.status === 409 || r.data.error === 'FINANCE_VERSION_PERIMEE') {
      setConflit(true)
      toast('error', 'Quelqu’un d’autre a modifié cette clôture entre-temps. Rien n’a été écrasé.')
      return false
    }
    toast('error', r.data.message ?? 'Cette modification n’a pas pu être enregistrée.')
    return false
  }, [detail, chargerDetail, toast])

  /* ─── les étapes, telles que la nuit les a réellement produites ─── */
  const d = detail.etat === 'ok' ? detail.valeur : null

  /* L'adresse dans sa forme lisible : la nuit et sa soirée. Une révision
     remplacée garde son identifiant — la nuit désigne la clôture COURANTE. */
  useEffect(() => {
    if (!d || d.status === 'SUPERSEDED') return
    const voulue = urlCloture(d.business_date, d.soirees)
    if (baseCloture() !== voulue) {
      const etape = lireEtapeUrl()
      const lisible = `${voulue}${etape ? `/${encodeURIComponent(etape)}` : ''}`
      window.history.replaceState(window.history.state, '', lisible)
      noterAdresse(lisible)
    }
  }, [d])

  /* AMORÇAGE AUTOMATIQUE — un brouillon qui n'a encore rien repris alors que la
     nuit annonce des montants (ou dont l'Accueil / le Vestiaire attendent une
     réponse évidente) est amorcé UNE fois, et l'écran le dit. Sans lui, chaque
     point de vente affichait « aucune source » avec le rapport sous les yeux. */
  const [amorce, setAmorce] = useState<'a-faire' | 'en-cours' | 'fait'>('a-faire')
  useEffect(() => {
    if (amorce !== 'a-faire' || !d || nuit.etat !== 'ok') return
    if (!doitAmorcer(d, nuit.valeur) || !peut('finance.closure.edit')) { setAmorce('fait'); return }
    setAmorce('en-cours')
    void adminMutate<{ error?: string; message?: string; ouverts?: string[]; applied_cents?: number; signals?: string[] }>(
      'POST', `/api/v1/finance/closings/${d.id}/amorcer`, {},
    ).then((r) => {
      setAmorce('fait')
      if (r.status >= 200 && r.status < 300 && r.data.error === undefined) {
        const repris = r.data.applied_cents ?? 0
        if (repris > 0 || (r.data.ouverts ?? []).length > 0) {
          toast('ok', repris > 0
            ? 'Montants de la nuit repris en source : chaque point de vente affiche maintenant ce que les rapports annoncent.'
            : 'Accueil et Vestiaire marqués ouverts.')
        }
        chargerDetail()
        chargerNuit()
      }
    })
  }, [amorce, d, nuit, peut, toast, chargerDetail, chargerNuit])
  const etapes = useMemo(() => {
    const l: EtapeAssistant[] = [{ cle: 'nuit', label: 'Nuit' }]
    // Une étape de point de vente s'appelle comme lui (« bar-room-1 ») ; deux
    // noms identiques reçoivent un suffixe, jamais l'identifiant.
    const prises = new Set(['nuit', 'ecarts', 'recap', 'synthese'])
    if (d) for (const p of d.pos) {
      if (p.was_open !== true) continue
      let cle = slugSoiree(p.point_of_sale_name) || 'point-de-vente'
      for (let i = 2; prises.has(cle); i++) cle = `${slugSoiree(p.point_of_sale_name)}-${i}`
      prises.add(cle)
      l.push({ cle, label: p.point_of_sale_name, posId: p.id })
    }
    /* Les écarts AVANT la synthèse : on explique ce qui cloche pendant qu'on a
       encore les chiffres sous les yeux, pas une fois la nuit récapitulée. */
    l.push({ cle: 'ecarts', label: 'Écarts' })
    // Facultatif : le récap des annexes ZAP (offerts, doses, bouteilles,
    // tiroirs, produits) se lit si l'on veut, avant d'arrêter la nuit.
    l.push({ cle: 'recap', label: 'Récap' })
    l.push({ cle: 'synthese', label: 'Synthèse' })
    return l
  }, [d])

  // Un ancien lien porte l'identifiant du point de vente : il mène à la même étape.
  const parId = etapes.find((e) => e.posId !== undefined && e.posId === etapeUrl)
  const courante = parId ? parId.cle : etapes.some((e) => e.cle === etapeUrl) ? etapeUrl! : 'nuit'
  const idx = etapes.findIndex((e) => e.cle === courante)

  /* L'URL dit toujours la vérité : une étape disparue (point de vente déclaré
     fermé) est corrigée SANS empiler d'historique. */
  useEffect(() => {
    if (d && etapeUrl !== courante) allerEtape(courante)
  }, [d, etapeUrl, courante])

  /* LE SENS DU PASSAGE — décidé au rendu, à partir de l'étape précédente.
     Le premier affichage d'une clôture chargée n'a pas de sens : l'entrée de
     la page (`lc-stagger`) suffit, l'étape ne glisse pas par-dessus. La
     mémoire est un objet stable que le rendu met à jour de façon idempotente :
     rejoué deux fois, il rend le même sens. */
  const passage = useRef<{ cle: string | null; idx: number; sens: 'avant' | 'arriere' | null }>({ cle: null, idx: -1, sens: null })
  if (!d) {
    passage.current = { cle: null, idx: -1, sens: null }
  } else if (passage.current.cle === null) {
    passage.current = { cle: courante, idx, sens: null }
  } else if (passage.current.cle !== courante) {
    passage.current = { cle: courante, idx, sens: sensDuPassage(passage.current.idx, idx) }
  }
  const sens = passage.current.sens

  /* L'Accueil (la billetterie) : c'est là que la guestlist se saisit. L'étape
     « Nuit » y renvoie d'un clic — par l'étape, pas par le routeur. */
  const etapeAccueil = d
    ? etapes.find((e) => e.posId !== undefined && d.pos.find((p) => p.id === e.posId)?.point_of_sale_kind === 'BILLETTERIE') ?? null
    : null

  /* L'état de chaque étape, lu dans les données (voir ClosingRail). */
  const seuils = useMemo(
    () => ({ warn: reglages.discrepancy_warn_cents, alert: reglages.discrepancy_alert_cents }),
    [reglages],
  )
  const etapesRail = useMemo(
    () => (d ? etatsDesEtapes(etapes, d, courante, seuils) : []),
    [etapes, d, courante, seuils],
  )

  /* Le contenu change SANS remonter la page : on ramène donc le haut de
     l'étape à l'écran quand on vient d'en changer — sinon « Suivant », cliqué
     dans le pied collant, ouvrirait l'étape suivante par son milieu. */
  const contenu = useRef<HTMLDivElement>(null)
  const etapePrecedente = useRef<string | null>(null)
  useEffect(() => {
    const avant = etapePrecedente.current
    etapePrecedente.current = courante
    if (avant === null || avant === courante) return
    const el = contenu.current
    const defilant = el?.closest('.lc-scroll')
    if (!el || !defilant) return
    if (el.getBoundingClientRect().top < defilant.getBoundingClientRect().top) {
      const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        || document.documentElement.dataset['motion'] === 'minimal'
      el.scrollIntoView({ block: 'start', behavior: reduit ? 'auto' : 'smooth' })
    }
  }, [courante])

  /* Un onglet qui dit de quelle nuit il s'agit : on en ouvre souvent deux.
     Posé APRÈS la coquille, qui donne à chaque ADRESSE son titre générique —
     y compris quand l'étape change l'adresse sur place (`noterAdresse`) :
     suivre le seul chemin laissait la coquille écraser ce titre. */
  const chemin = usePath()
  const adresseAffichee = useAdresseCourante(chemin)
  const nuitDuTitre = d?.business_date ?? null
  useEffect(() => {
    if (!nuitDuTitre) return
    const t = window.setTimeout(() => setPageTitle(`Clôture · ${nuitCourte(nuitDuTitre).toLowerCase()}`), 0)
    return () => window.clearTimeout(t)
  }, [nuitDuTitre, adresseAffichee])

  const recharger = useCallback(() => { chargerDetail(); chargerNuit() }, [chargerDetail, chargerNuit])
  const api: ClosingApi | null = d ? { closing: d, settings: reglages, peut, ecrire, recharger } : null
  // Le geste de clôture (déclarer prête, finaliser, le sceau) : avant les
  // retours anticipés — un hook ne se saute pas. L'aperçu attend les droits.
  const geste = useGesteCloture(api, { apercu: apercu && !droitsEnCours })

  /* Les PIÈCES D'ARGENT de la nuit — rapports ZAP et redditions Shotgun. Les
     annexes (doseurs, tiroirs, TVA…) restent des preuves, repliées à part. */
  const pieces: DocVisible[] = useMemo(() => {
    if (nuit.etat !== 'ok') return []
    const n = nuit.valeur
    return [
      ...n.reports.map((r) => ({
        id: r.document_id, filename: r.filename, provider: 'ZAP_SIS', report_type: 'ZAP_REGISTER_SUMMARY',
        business_date: n.business_date, received_at: r.received_at,
        libelle: r.role === 'PRINCIPAL' ? 'Rapport de caisse ZAP' : 'Complément hors ligne ZAP',
      })),
      ...n.redditions.filter((x) => !x.ignored).map((x) => ({
        id: x.document_id, filename: x.filename, provider: 'SHOTGUN', report_type: 'SHOTGUN_EVENT_REPORT',
        business_date: n.business_date, libelle: `Reddition Shotgun${x.report_id ? ` ${x.report_id}` : ''}`,
      })),
    ]
  }, [nuit])
  const posCourant = d?.pos.find((p) => p.id === etapes.find((e) => e.cle === courante)?.posId) ?? null
  const posOuverts = d?.pos.filter((p) => p.was_open === true) ?? []

  /* ═══════════════════════ rendu ═══════════════════════ */

  if (detail.etat === 'chargement') {
    return (
      <main className="lc-page lc-page--large lc-stagger fh-page fwz-page">
        <SmartBack />
        <section className="lc-panel"><div className="lc-panel__body"><EnCours quoi="de la clôture" /></div></section>
      </main>
    )
  }

  if (detail.etat === 'absent' || !d || !api) {
    return (
      <main className="lc-page lc-page--large lc-stagger fh-page fwz-page">
        <SmartBack />
        <section className="lc-panel">
          <div className="lc-panel__body">
            <Indisponible
              titre="Cette clôture n’a pas pu être ouverte"
              message={detail.etat === 'absent' ? detail.message : 'Réponse inattendue du serveur.'}
            >
              {nuitRef ? (
                <Link className="lc-btn lc-btn--sm" href={`/comptabilite/nuits/${nuitRef}`}>Voir la nuit</Link>
              ) : (
                <button className="lc-btn lc-btn--sm fih-retry" onClick={chargerDetail}>Réessayer</button>
              )}
            </Indisponible>
          </div>
        </section>
      </main>
    )
  }

  const figee = d.status === 'CLOSED' || d.status === 'SUPERSEDED'

  return (
    <main className="lc-page lc-page--large lc-stagger fh-page fiw-page fwz-page">
      {/* ─── la nuit : visuel de la soirée, état, chiffres clés ─── */}
      <ClosingHero closing={d} onIgnoree={recharger} />

      {/* ÉDITION SIMULTANÉE — dire AVANT la collision, pas après (§229).
          On n'affiche rien si le dernier écrivain est l'utilisateur courant :
          se signaler à soi-même serait du bruit. Et on ne verrouille rien —
          la lecture reste libre, l'écriture reste arbitrée par la version. */}
      {!conflit && d.locked_by_name && !d.locked_is_me ? (
        <div className="fiw-conflit fiw-conflit--info" role="status">
          <Users size={17} aria-hidden="true" />
          <span>
            {d.locked_by_name} a modifié cette clôture{' '}
            {d.locked_at ? `le ${new Date(d.locked_at).toLocaleString('fr-FR')}` : 'récemment'}.
            Vous pouvez la consulter et la compléter : chaque enregistrement
            vérifie que vous partez bien de la dernière version.
          </span>
        </div>
      ) : null}

      {conflit ? (
        <div className="fiw-conflit" role="status">
          <AlertTriangle size={17} aria-hidden="true" />
          <span>
            Cette clôture a été modifiée ailleurs pendant que vous y travailliez.
            Votre dernière saisie n’a pas été enregistrée, et rien du travail de
            l’autre personne n’a été écrasé.
          </span>
          <button className="lc-btn lc-btn--sm" onClick={() => { setGraine((g) => g + 1); chargerDetail() }}>
            <RefreshCw size={15} aria-hidden="true" />Reprendre la version à jour
          </button>
        </div>
      ) : null}

      {figee ? (
        <div className="fiw-fige" role="status">
          <Check size={17} aria-hidden="true" />
          <span>
            {d.status === 'CLOSED'
              ? `Nuit finalisée ${ilYA(d.closed_at)}${d.closed_by_name ? ` par ${d.closed_by_name}` : ''}. Elle ne se modifie plus : la corriger crée une révision qui laisse celle-ci intacte.`
              : 'Cette révision a été remplacée par une plus récente. Elle reste lisible telle qu’elle a été arrêtée.'}
          </span>
        </div>
      ) : null}

      {/* ─── deux colonnes : le rail des étapes, l'étape ─── */}
      <div className="fwz-corps">
        <ClosingRail etapes={etapesRail} courante={courante} seuils={seuils} onEtape={allerEtape} />

        <div className="fwz-contenu" ref={contenu}>
          {/* L'étape, et elle seule : le bloc renaît à chaque changement
              d'étape et entre dans le sens de la marche. Le pied reste dehors. */}
          <div key={courante} className={`fwz-etape${sens ? ` fwz-etape--${sens}` : ''}`}>
          {courante === 'nuit' ? (
            // Remontée par la graine comme les autres étapes : un brouillon de
            // saisie doit repartir de la version fraîche après un conflit.
            <ClosingStepNight
              key={`nuit-${graine}`}
              api={api}
              nuit={nuit}
              pieces={pieces}
              amorcage={amorce === 'en-cours'}
              posDispo={posDispo}
              figee={figee}
              onAccueil={etapeAccueil ? () => allerEtape(etapeAccueil.cle) : null}
            />
          ) : null}

          {posCourant ? (
            <ClosingStepPos
              key={`${posCourant.id}-${graine}`}
              api={api}
              pos={posCourant}
              terminaux={terminaux}
              moyens={moyens}
              figee={figee}
              rang={posOuverts.findIndex((p) => p.id === posCourant.id) + 1}
              total={posOuverts.length}
            />
          ) : null}

          {courante === 'ecarts' ? (
            <ClosingStepEcarts
              key={`ecarts-${graine}`}
              api={api}
              motifs={motifsEcart}
              figee={figee}
              onEtape={(cle) => allerEtape(cle)}
            />
          ) : null}

          {courante === 'recap' ? <NightRecap nuit={d.business_date} /> : null}

          {courante === 'synthese' ? (
            <ClosingStepSummary
              api={api}
              pieces={pieces}
              figee={figee}
              onEtape={(cle) => allerEtape(cle)}
              nuit={nuit}
            />
          ) : null}
          </div>

          {/* ─── pied stable : où j'en suis, et où je vais ─── */}
          <footer className="fiw-foot fwz-pied">
            <button
              type="button"
              className="lc-btn lc-btn--ghost fdc-precedent"
              disabled={idx <= 0}
              onClick={() => { const p = etapes[idx - 1]; if (p) allerEtape(p.cle) }}
            >
              <ArrowLeft size={16} aria-hidden="true" /><span className="fdc-libelle">Précédent</span>
            </button>

            <span className="fiw-foot__etat" aria-live="polite">
              <EtatDuPied api={api} courante={courante} sauvegarde={sauvegarde} onSynthese={() => allerEtape('synthese')} />
            </span>

            {/* À la synthèse, le « Suivant » grisé laisse la place au geste de
                la clôture : déclarer prête, finaliser, ou la fiche de la nuit. */}
            {courante === 'synthese' ? <DecisionCloture api={api} geste={geste} /> : (
              <button
                type="button"
                className="lc-btn lc-btn--primary"
                disabled={idx >= etapes.length - 1}
                onClick={() => { const n = etapes[idx + 1]; if (n) allerEtape(n.cle) }}
              >
                {idx === etapes.length - 2 ? 'Aller à la synthèse' : 'Suivant'}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            )}
          </footer>
        </div>
      </div>
    </main>
  )
}
